import React, { useCallback, useRef } from "react";
import useNotification from "../../hooks/useNotification";
import useTranslations from "../../hooks/useTranslations";
import useDirectUpload from "../../hooks/useDirectUpload";
import { PlusIcon } from "../icons";
import { Button, H4 } from "../wombat-ui";
import { DocumentPlate } from "../utils/DocumentPlate";
import { UserReviewFragment, useAddUserDocument, useDeleteUserDocument, GetUserDocument } from '../../graphql/generated/definitions';

type UserDocumentsSectionProps = {
  user: UserReviewFragment;
  vertical: boolean;
};

const UserDocumentsSection: React.FC<UserDocumentsSectionProps> = ({
  user,
  vertical,
}) => {
  const t = useTranslations();

  const fileUploadField = useRef<HTMLInputElement>();

  const [
    addUserDocument,
    { loading: addUserDocumentLoading },
  ] = useAddUserDocument({
    refetchQueries: [{ query: GetUserDocument, variables: { id: user.id } }],
  });
  const { notifyError } = useNotification();

  const [deleteUserDocument, { loading }] = useDeleteUserDocument({
    refetchQueries: [{ query: GetUserDocument, variables: { id: user.id } }],
  });

  const handleDelete = useCallback(({ id }) => {
    deleteUserDocument({
      variables: { input: { id } },
    })
  }, [deleteUserDocument]);

  const [ upload, { loading: directUploadLoading } ] = useDirectUpload();

  const handleUpload = useCallback(async (e) => {
    try {
      const { signed_id: signedId } = await upload(e.target?.files?.[0])
      await addUserDocument({
        variables: {
          input: {
            id: user.id,
            signedId
          },
        },
      })
    } catch (e) {
      notifyError({
        title: t("forms.errors.notificationTitle") as string,
        description: t("forms.errors.invalidFile") as string,
      });
    }
  }, [user.id]);

  if (!user) {
    return null;
  }

  return (
    <div className={vertical ? undefined : "flex items-center space-x-6 pb-6"}>
      <div
        className={
          vertical
            ? "flex items-end justify-between pb-3"
            : "flex items-center space-x-6"
        }
      >
        <div>
          <H4>{t("collaborator.documents")}</H4>
        </div>
        {user.canAddDocument && (
          <div>
            <input
              ref={fileUploadField}
              type="file"
              onChange={handleUpload}
              name="document"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                margin: 0,
                padding: 0,
                opacity: 0,
                width: 1,
                height: 1,
              }}
            />
            <Button
              disabled={addUserDocumentLoading || directUploadLoading}
              variant="subtle"
              loading={addUserDocumentLoading || directUploadLoading}
              id="addDocument"
              onClick={() => fileUploadField?.current?.click()}
              icon={PlusIcon}
              scale="xs"
            />
          </div>
        )}
      </div>
      <div>
        {user.documents.length === 0 ? (
          <div className="pt-1 text-sm text-light-900">
            <p>{t("collaborator.noDocuments")}</p>
          </div>
        ) : (
          <ul
            id="documentPlates"
            className={
              vertical
                ? "space-y-2"
                : "grid gap-4 lg:grid-cols-4 xs:grid-cols-1 md:grid-cols-2"
            }
          >
            {user.documents.map((document) => (
              <DocumentPlate
                key={document.id}
                allowDelete={user.canAddDocument}
                document={document}
                onDelete={handleDelete}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UserDocumentsSection;
